// frontend/src/components/common/Toast.jsx
// Sistema de notificaciones no invasivo — reemplaza alert() del browser
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext(null);

// Duración en ms antes de auto-dismiss. null = solo manual.
const DURACIONES = {
  success: 3500,
  info:    3500,
  warning: 5000,
  error:   8000,
};

let _nextId = 0;

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const eliminar = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const agregar = useCallback((tipo, mensaje, duracion) => {
    const id = ++_nextId;
    const dur = duracion !== undefined ? duracion : DURACIONES[tipo];
    setToasts(prev => [...prev, { id, tipo, mensaje }]);
    if (dur) {
      timersRef.current[id] = setTimeout(() => eliminar(id), dur);
    }
    return id;
  }, [eliminar]);

  // Limpieza al desmontar
  useEffect(() => {
    const timers = timersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  const toast = {
    success: (msg, dur) => agregar('success', msg, dur),
    error:   (msg, dur) => agregar('error',   msg, dur),
    warning: (msg, dur) => agregar('warning', msg, dur),
    info:    (msg, dur) => agregar('info',    msg, dur),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {ReactDOM.createPortal(
        <ToastContainer>
          {toasts.map(t => (
            <ToastItem key={t.id} $tipo={t.tipo} onClose={() => eliminar(t.id)}>
              {t.mensaje}
            </ToastItem>
          ))}
        </ToastContainer>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de ToastProvider');
  return ctx;
};

// ─── Animations ───────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(calc(100% + 16px)); opacity: 0; }
  to   { transform: translateX(0);                 opacity: 1; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  position: fixed;
  bottom: calc(${({ theme }) => theme.heights.footer} + 12px);
  right: 16px;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  z-index: 1080;
  pointer-events: none;
`;

const ACCENTS = {
  success: t => t.colors.success,
  error:   t => t.colors.danger,
  warning: t => t.colors.warning,
  info:    t => t.colors.info,
};

const ICONS = {
  success: 'bi-check-circle-fill',
  error:   'bi-x-circle-fill',
  warning: 'bi-exclamation-triangle-fill',
  info:    'bi-info-circle-fill',
};

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 260px;
  max-width: 380px;
  padding: 12px 14px 12px 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 4px solid ${({ $tipo, theme }) => (ACCENTS[$tipo] || ACCENTS.info)(theme)};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
  pointer-events: all;
  animation: ${slideIn} 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const IconWrap = styled.i`
  font-size: 15px;
  flex-shrink: 0;
  margin-top: 1px;
  color: ${({ $tipo, theme }) => (ACCENTS[$tipo] || ACCENTS.info)(theme)};
`;

const MensajeTexto = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.45;
  font-family: ${({ theme }) => theme.typography.fontFamily};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.5;
  font-size: 13px;
  transition: opacity 0.15s;
  margin-top: 1px;

  &:hover { opacity: 1; }
`;

// ─── Sub-componentes ──────────────────────────────────────────────────────────

const ToastContainer = ({ children }) => <Container>{children}</Container>;

const ToastItem = ({ $tipo, onClose, children }) => (
  <Item $tipo={$tipo}>
    <IconWrap className={`bi ${ICONS[$tipo] || ICONS.info}`} $tipo={$tipo} />
    <MensajeTexto>{children}</MensajeTexto>
    <CloseBtn onClick={onClose} title="Cerrar"><i className="bi bi-x" /></CloseBtn>
  </Item>
);
